/**
 * Vol/return scatter: domain from the row set, then domain to pixels (F6).
 *
 * Two functions rather than one, because the domain is a property of the DATA
 * and the geometry is a property of the CHART — the sector screen reuses the
 * same plot box with a different row set.
 *
 * The zero line is CLAMPED into the plot. The return domain is `[min, max]` over
 * the filtered rows, so in a uniformly positive tape zero sits BELOW the plot
 * and an unclamped baseline renders outside its own axes, overlapping the
 * x-axis labels. Clamping pins it to the nearer edge, which reads correctly:
 * every point really is above (or below) zero.
 */
export interface ScatterPoint {
  readonly vol: number;
  readonly ret: number;
}

export interface ScatterDomain {
  readonly volLo: number;
  readonly volHi: number;
  readonly retLo: number;
  readonly retHi: number;
}

export interface ScatterScale {
  readonly x: (vol: number) => number;
  readonly y: (ret: number) => number;
  /** `y(0)`, clamped into `[plotTop, plotBottom]`. */
  readonly zeroY: number;
}

/** Gutter between the plot box and the SVG viewport edge, in user units. */
const VIEWBOX_MARGIN_RIGHT = 8;
/** Room below the plot for the vol-axis label row (`plotBottom + 20` baseline). */
const VIEWBOX_MARGIN_BOTTOM = 26;

/**
 * Plot box in SVG user units (demo lines 900–905).
 *
 * `viewBoxWidth`/`viewBoxHeight` are DERIVED here rather than written as a
 * literal in the component: a hand-written `viewBox` silently clips every
 * right-hand point and the vol-hi axis label the moment `plotRight` grows, with
 * no type error and no failing test.
 */
export const SCATTER_GEOMETRY = Object.freeze({
  plotLeft: 34,
  plotRight: 332,
  plotTop: 14,
  plotBottom: 200,
  viewBoxWidth: 332 + VIEWBOX_MARGIN_RIGHT,
  viewBoxHeight: 200 + VIEWBOX_MARGIN_BOTTOM,
});

/**
 * Point radius from market cap. Lives here, not in the component: it is pure
 * geometry and belongs under core's 95% gate with the rest of the plot maths.
 * A null/unknown cap collapses to the minimum radius.
 */
export const SCATTER_RADIUS_MIN = 1.6;
export const SCATTER_RADIUS_MAX_GAIN = 4.4;
export const SCATTER_RADIUS_DIVISOR = 12;

export function scatterPointRadius(mcap: number | null): number {
  if (mcap === null || !Number.isFinite(mcap)) return SCATTER_RADIUS_MIN;
  return (
    SCATTER_RADIUS_MIN +
    Math.min(SCATTER_RADIUS_MAX_GAIN, Math.sqrt(Math.max(0, mcap)) / SCATTER_RADIUS_DIVISOR)
  );
}

/** Vol axis starts at a fixed floor, not at the observed minimum. */
export const SCATTER_VOL_FLOOR = 0.05;
/** Headroom so the widest-vol point is not on the frame. */
export const SCATTER_VOL_HEADROOM = 1.05;

export function scatterDomain(points: readonly ScatterPoint[]): ScatterDomain {
  const first = points[0];
  if (first === undefined) {
    throw new Error('scatterDomain: at least one point is required');
  }
  const rets = points.map((p) => p.ret);
  const maxVol = points.reduce((a, p) => Math.max(a, p.vol), first.vol);
  return {
    volLo: SCATTER_VOL_FLOOR,
    /* Never invert: live low-vol filters can sit entirely below the floor. */
    volHi: Math.max(SCATTER_VOL_FLOOR, maxVol * SCATTER_VOL_HEADROOM),
    retLo: rets.reduce((a, r) => Math.min(a, r), first.ret),
    retHi: rets.reduce((a, r) => Math.max(a, r), first.ret),
  };
}

export function scatterScale(domain: ScatterDomain): ScatterScale {
  const { plotLeft, plotRight, plotTop, plotBottom } = SCATTER_GEOMETRY;
  /* Zero span collapses onto the edge; NaN does NOT get the same treatment.
     `||` would silently rescue it (NaN is falsy) and render a plausible plot
     from a corrupt domain — the same failure `corr` refuses by throwing rather
     than emitting NaN (correlation.ts). */
  const span = (hi: number, lo: number): number => {
    const d = hi - lo;
    if (Number.isNaN(d)) throw new Error('scatterScale: domain is NaN');
    return d === 0 ? 1 : d;
  };
  const volSpan = span(domain.volHi, domain.volLo);
  const retSpan = span(domain.retHi, domain.retLo);
  const plotHeight = plotBottom - plotTop;
  const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
  /* x is CLAMPED, y is NOT — deliberately asymmetric. `volLo` is a fixed FLOOR
     (SCATTER_VOL_FLOOR), not the observed minimum, so a name quieter than the
     floor scales to a negative offset and draws outside the viewBox: invisible,
     yet still holding a focus stop and a line in the a11y summary. The return
     domain is `[min, max]` OVER THE POINTS, so no point can fall outside it, and
     raw `y` stays unclamped so `y(0)` keeps reporting where zero truly lies —
     that is precisely what `zeroY` clamps and what layout.test.ts pins. */
  const x = (vol: number): number =>
    clamp(
      plotLeft + ((vol - domain.volLo) / volSpan) * (plotRight - plotLeft),
      plotLeft,
      plotRight,
    );
  const y = (ret: number): number => plotBottom - ((ret - domain.retLo) / retSpan) * plotHeight;
  return { x, y, zeroY: clamp(y(0), plotTop, plotBottom) };
}
